export type PortfolioFilterGroup = "quick" | "top" | "accountList" | "health" | "governance";

export type PortfolioFilterSelections = {
  quick: string;
  top?: string;
  accountList?: string;
  health?: string;
  governance?: string;
};

export const DEFAULT_PORTFOLIO_QUICK_FILTER = "My Accounts";

export const DEFAULT_PORTFOLIO_FILTER_SELECTIONS: PortfolioFilterSelections = {
  quick: DEFAULT_PORTFOLIO_QUICK_FILTER,
};

export const PORTFOLIO_FILTER_GROUP_ORDER: PortfolioFilterGroup[] = [
  "quick",
  "top",
  "accountList",
  "health",
  "governance",
];

export const PORTFOLIO_FILTER_MODEL_KEYS: Record<PortfolioFilterGroup, string> = {
  quick: "externalFilter",
  top: "externalTopFilter",
  accountList: "externalAccountListFilter",
  health: "externalHealthFilter",
  governance: "externalAlertFilter",
};

export const PORTFOLIO_QUICK_FILTER_LABELS = [
  "My Accounts",
  "My Team",
  "New Accounts",
  "Favorites",
];

export const PORTFOLIO_TOP_FILTER_LABELS = ["Top 150", "Top 500"];

export const PORTFOLIO_ACCOUNT_LIST_FILTER_LABELS = [
  "High Touch",
  "HT w/ Renewals >6mo",
  "Big Deals",
  "Device Seeds",
  "Webex Suite",
  "No Auto Renewal",
  "Zero Renewals",
];

export const PORTFOLIO_HEALTH_FILTER_LABELS = [
  "Portfolio Significant",
  "Portfolio Moderate",
  "Portfolio Minimal",
];

export const PORTFOLIO_GOVERNANCE_FILTER_LABELS = [
  "AMER",
  "EMEA",
  "APJC",
  "renewalAssessment_AMER",
  "renewalAssessment_EMEA",
  "renewalAssessment_APJC",
];

const PORTFOLIO_QUICK_FILTER_COUNTS_QUERY: Record<string, Record<string, string>> = {
  "My Accounts": { viewType: "my" },
  "My Team": { viewType: "team" },
  "New Accounts": { viewType: "my", newAccount: "1" },
  Favorites: { viewType: "favorite" },
};

const PORTFOLIO_TOP_COUNTS_QUERY: Record<string, Record<string, string>> = {
  "Top 150": { priorityIndicatorRank: "lt:151" },
  "Top 500": { priorityIndicatorRank: "lt:501" },
};

const PORTFOLIO_ACCOUNT_LIST_COUNTS_QUERY: Record<string, Record<string, string>> = {
  "High Touch": { highTouch: "1" },
  "Big Deals": { bigDeals: "1" },
  "Webex Suite": { webexSuiteFlag: "1" },
  "HT w/ Renewals >6mo": { highTouchRenewals: "1" },
  "Device Seeds": { deviceSeeds: "1" },
  "No Auto Renewal": { zeroRenewalTerm: "1" },
};

const PORTFOLIO_HEALTH_COUNTS_QUERY: Record<string, Record<string, string>> = {
  "Portfolio Significant": { expandPlanRiskAssessment: "Significant Risk" },
  Significant: { expandPlanRiskAssessment: "Significant Risk" },
  "Significant Risk": { expandPlanRiskAssessment: "Significant Risk" },
  "Portfolio Moderate": { expandPlanRiskAssessment: "Moderate Risk" },
  Moderate: { expandPlanRiskAssessment: "Moderate Risk" },
  "Moderate Risk": { expandPlanRiskAssessment: "Moderate Risk" },
  "Portfolio Minimal": { expandPlanRiskAssessment: "Minimal Risk" },
  Minimal: { expandPlanRiskAssessment: "Minimal Risk" },
  "Minimal Risk": { expandPlanRiskAssessment: "Minimal Risk" },
};

const PORTFOLIO_GOVERNANCE_COUNTS_QUERY: Record<string, Record<string, string>> = {
  AMER: { epRequiredAMER: "y" },
  EMEA: { epRequiredEMEA: "y" },
  APJC: { epRequiredAPJC: "y" },
  renewalAssessment_AMER: { assessRequiredAMER: "y" },
  renewalAssessment_EMEA: { assessRequiredEMEA: "y" },
  renewalAssessment_APJC: { assessRequiredAPJC: "y" },
};

const LABELS_BY_GROUP: Record<PortfolioFilterGroup, string[]> = {
  quick: PORTFOLIO_QUICK_FILTER_LABELS,
  top: PORTFOLIO_TOP_FILTER_LABELS,
  accountList: PORTFOLIO_ACCOUNT_LIST_FILTER_LABELS,
  health: PORTFOLIO_HEALTH_FILTER_LABELS,
  governance: PORTFOLIO_GOVERNANCE_FILTER_LABELS,
};

export const getPortfolioFilterGroupByLabel = (
  label?: string
): PortfolioFilterGroup | undefined => {
  if (!label || label === "init") return undefined;
  return PORTFOLIO_FILTER_GROUP_ORDER.find((group) => LABELS_BY_GROUP[group].includes(label));
};

export const normalizePortfolioFilterSelections = (
  selections?: Partial<PortfolioFilterSelections>
): PortfolioFilterSelections => {
  const normalized: PortfolioFilterSelections = {
    quick: selections?.quick || DEFAULT_PORTFOLIO_QUICK_FILTER,
  };

  PORTFOLIO_FILTER_GROUP_ORDER.forEach((group) => {
    if (group === "quick") return;
    const label = selections?.[group];
    if (label) normalized[group] = label;
  });

  return normalized;
};

export const getPortfolioFilterSelectionKey = (selections?: Partial<PortfolioFilterSelections>) => {
  const normalized = normalizePortfolioFilterSelections(selections);
  return PORTFOLIO_FILTER_GROUP_ORDER.map((group) => normalized[group] ?? "").join("|");
};

export const arePortfolioFilterSelectionsEqual = (
  a?: Partial<PortfolioFilterSelections>,
  b?: Partial<PortfolioFilterSelections>
) => getPortfolioFilterSelectionKey(a) === getPortfolioFilterSelectionKey(b);

export const buildPortfolioFilterModel = (selections?: Partial<PortfolioFilterSelections>) => {
  const normalized = normalizePortfolioFilterSelections(selections);
  return PORTFOLIO_FILTER_GROUP_ORDER.reduce(
    (model, group) => {
      const label = normalized[group];
      if (label) {
        model[PORTFOLIO_FILTER_MODEL_KEYS[group]] = {
          filter: label,
          filterType: "text",
          type: "contains",
        };
      }
      return model;
    },
    {} as Record<string, { filter: string; filterType: "text"; type: "contains" }>
  );
};

export const removePortfolioFilterModelKeys = <T extends Record<string, any>>(filterModel: T) => {
  const next = { ...filterModel };
  Object.values(PORTFOLIO_FILTER_MODEL_KEYS).forEach((key) => {
    delete next[key];
  });
  return next;
};

export const getPortfolioFilterSelectionsFromModel = (
  filterModel?: Record<string, any>
): PortfolioFilterSelections => {
  const selections: Partial<PortfolioFilterSelections> = {};

  if (!filterModel) return normalizePortfolioFilterSelections(selections);

  PORTFOLIO_FILTER_GROUP_ORDER.forEach((group) => {
    const label = filterModel[PORTFOLIO_FILTER_MODEL_KEYS[group]]?.filter;
    if (label && getPortfolioFilterGroupByLabel(label) === group) {
      selections[group] = label;
    }
  });

  const legacyExternalFilter = filterModel.externalFilter?.filter;
  const legacyGroup = getPortfolioFilterGroupByLabel(legacyExternalFilter);
  if (legacyExternalFilter && legacyGroup) {
    selections[legacyGroup] = legacyExternalFilter;
  }

  const legacyAlertFilter = filterModel.externalAlertFilter?.filter;
  if (legacyAlertFilter) {
    selections.governance = legacyAlertFilter;
  }

  return normalizePortfolioFilterSelections(selections);
};

export const portfolioFilterModelMatchesSelections = (
  filterModel: Record<string, any> | undefined,
  selections?: Partial<PortfolioFilterSelections>
) => {
  const normalized = normalizePortfolioFilterSelections(selections);
  return PORTFOLIO_FILTER_GROUP_ORDER.every((group) => {
    const key = PORTFOLIO_FILTER_MODEL_KEYS[group];
    const expected = normalized[group] ?? "";
    const actual = filterModel?.[key]?.filter ?? "";
    return expected === actual;
  });
};

export const buildPortfolioCountsQuery = (
  selections?: Partial<PortfolioFilterSelections>,
  groups: readonly PortfolioFilterGroup[] = PORTFOLIO_FILTER_GROUP_ORDER
) => {
  if (!selections) return "";

  const normalized = normalizePortfolioFilterSelections(selections);
  const params = new URLSearchParams();
  const setParams = (queryParams: Record<string, string>) => {
    Object.entries(queryParams).forEach(([key, value]) => {
      params.set(key, value);
    });
  };

  groups.forEach((group) => {
    const label = normalized[group];
    if (!label) return;

    if (group === "quick") {
      const quickParams = PORTFOLIO_QUICK_FILTER_COUNTS_QUERY[label];
      if (quickParams) {
        setParams(quickParams);
        return;
      }
    }

    if (group === "top") {
      const topParams = PORTFOLIO_TOP_COUNTS_QUERY[label];
      if (topParams) {
        setParams(topParams);
        return;
      }
    }

    if (group === "accountList") {
      const accountListParams = PORTFOLIO_ACCOUNT_LIST_COUNTS_QUERY[label];
      if (accountListParams) {
        setParams(accountListParams);
        return;
      }
    }

    if (group === "health") {
      const healthParams = PORTFOLIO_HEALTH_COUNTS_QUERY[label];
      if (healthParams) {
        setParams(healthParams);
        return;
      }
    }

    if (group === "governance") {
      const governanceParams = PORTFOLIO_GOVERNANCE_COUNTS_QUERY[label];
      if (governanceParams) {
        setParams(governanceParams);
        return;
      }
    }

    params.set(PORTFOLIO_FILTER_MODEL_KEYS[group], label);
  });

  const query = params.toString();
  return query ? `?${query}` : "";
};
